import { api } from '@/lib/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@iq/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function DangerZone() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onConfirmDelete() {
    setPending(true);
    setError(null);
    const { error: apiError } = await api.POST('/account/delete');
    if (apiError) {
      setPending(false);
      setError(apiError.detail ?? apiError.title ?? t('settings.danger.error'));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
    navigate({ to: '/signin' });
  }

  return (
    <Card data-testid="settings-danger" className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive">{t('settings.danger.title')}</CardTitle>
        <CardDescription>{t('settings.danger.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button type="button" variant="destructive" data-testid="danger-trigger">
              <Trash2 className="mr-2 h-4 w-4" />
              {t('settings.danger.action')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('settings.danger.confirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('settings.danger.confirmBody')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('settings.danger.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={onConfirmDelete}
                disabled={pending}
                data-testid="danger-confirm"
              >
                {pending ? t('settings.danger.pending') : t('settings.danger.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
